import { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, X, ShoppingCart } from "lucide-react";
import { useCart, fmt } from "../../context/CartContext";
import { useCommerce } from "../../context/CommerceContext";

export const QuantityControl = ({ value, onChange, max, idPrefix }) => (
  <div className="inline-flex items-center border border-white/20 bg-[#0A0A0A]" data-testid={`${idPrefix}-quantity`}>
    <button type="button" aria-label="Decrease quantity" onClick={() => onChange(value - 1)} className="px-3 py-2 text-gray-300 hover:text-signal-red disabled:opacity-30" disabled={value <= 1} data-testid={`${idPrefix}-decrease`}>
      <Minus className="w-4 h-4" />
    </button>
    <input
      type="number" min={1} max={max} value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-14 bg-transparent text-center text-white font-bold focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      data-testid={`${idPrefix}-quantity-input`}
    />
    <button type="button" aria-label="Increase quantity" onClick={() => onChange(value + 1)} className="px-3 py-2 text-gray-300 hover:text-signal-red disabled:opacity-30" disabled={value >= max} data-testid={`${idPrefix}-increase`}>
      <Plus className="w-4 h-4" />
    </button>
  </div>
);

export const CartLines = ({ compact = false }) => {
  const { lines, setQuantity, removeItem, MAX_QTY } = useCart();
  return (
    <ul className="divide-y divide-white/10" data-testid="cart-lines">
      {lines.map((l) => (
        <li key={l.sku} className="py-4 flex gap-4" data-testid={`cart-line-${l.sku}`}>
          {!compact && <Link to={l.to} className="w-20 h-20 flex-shrink-0 overflow-hidden border border-white/10 hidden sm:block"><img src={l.image} alt={`${l.name} (${l.model})`} className="w-full h-full object-cover" /></Link>}
          <div className="flex-1 min-w-0">
            <Link to={l.to} className="font-heading text-lg font-bold text-white hover:text-signal-red transition-colors" data-testid={`cart-line-${l.sku}-name`}>{l.name}</Link>
            <p className="font-mono text-gray-500 uppercase tracking-[0.2em] text-xs">{l.model} · {l.sku}</p>
            <p className="text-gray-400 text-sm mt-1" data-testid={`cart-line-${l.sku}-unit`}>{fmt(l.unit)}{l.unitLabel}</p>
            <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
              <QuantityControl value={l.quantity} max={MAX_QTY} onChange={(q) => setQuantity(l.sku, q)} idPrefix={`cart-line-${l.sku}`} />
              <div className="flex items-center gap-4">
                <span className="font-heading text-lg font-black text-white" data-testid={`cart-line-${l.sku}-total`}>{fmt(l.lineTotal)}</span>
                <button type="button" aria-label={`Remove ${l.name}`} onClick={() => removeItem(l.sku)} className="text-gray-500 hover:text-signal-red transition-colors" data-testid={`cart-line-${l.sku}-remove`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
};

export const CartDrawer = () => {
  const { isOpen, setIsOpen, lines, subtotal, count } = useCart();
  const { commerceEnabled } = useCommerce();
  if (!isOpen || !commerceEnabled) return null;
  return (
    <div className="fixed inset-0 z-[60]" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/70" onClick={() => setIsOpen(false)} />
      <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-[#141414] border-l border-white/10 flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h2 className="font-heading text-xl font-bold uppercase text-white flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-signal-red" /> Cart <span className="text-gray-500 text-sm font-mono">({count})</span></h2>
          <button type="button" aria-label="Close cart" onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white" data-testid="cart-drawer-close"><X className="w-6 h-6" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5">
          {lines.length === 0 ? (
            <p className="text-gray-400 py-10 text-center" data-testid="cart-empty">Your cart is empty.</p>
          ) : <CartLines compact />}
        </div>
        <div className="p-5 border-t border-white/10 space-y-3">
          <div className="flex justify-between text-white"><span className="text-gray-400">Subtotal</span><span className="font-heading text-xl font-black" data-testid="cart-drawer-subtotal">{fmt(subtotal)}</span></div>
          <p className="text-gray-500 text-xs">Shipping and tax are calculated at checkout.</p>
          <Link to="/cart" onClick={() => setIsOpen(false)} className="block text-center w-full px-6 py-3 bg-signal-red text-white font-bold uppercase tracking-wide hover:bg-signal-red/90 transition-colors" data-testid="cart-drawer-view-cart">View Cart & Checkout</Link>
          <button type="button" onClick={() => setIsOpen(false)} className="block w-full text-center text-gray-300 hover:text-white text-sm uppercase tracking-wide" data-testid="cart-drawer-continue">Continue Shopping</button>
        </div>
      </aside>
    </div>
  );
};

export const AddToCart = ({ product, idPrefix }) => {
  const { addItem, MAX_QTY } = useCart();
  const { commerceEnabled } = useCommerce();
  const [qty, setQty] = useState(1);
  if (!commerceEnabled) return null;
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`${idPrefix}-add-to-cart-block`}>
      <QuantityControl value={qty} max={MAX_QTY} onChange={(v) => setQty(Math.max(1, Math.min(MAX_QTY, Math.floor(Number(v) || 1))))} idPrefix={idPrefix} />
      <button type="button" onClick={() => addItem(product.sku, qty)} className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-signal-red text-white font-bold uppercase text-sm tracking-wide hover:bg-signal-red/90 transition-colors" data-testid={`${idPrefix}-add-to-cart`}>
        <ShoppingCart className="w-4 h-4" /> Add to Cart
      </button>
    </div>
  );
};
